import { PAGE_ROUTE } from '@/app/pageTypes';

// Some feed hosts 301 onto a locale-prefixed path (booking-dp inserts /en-gb) before the legacy path,
// which used to leave the request on a 404. Strip one leading locale segment before matching.
const LEADING_LOCALE = /^\/[a-z]{2}(-[a-zA-Z]{2})?(?=\/)/;

export const stripLeadingLocale = (pathname: string): string => pathname.replace(LEADING_LOCALE, '');

export const DETAILS_HOTEL_PREFIX = '/details/hotel/';

// Sub-pages that TSX serves itself under src/app/[id]/, and that hotel-detail also serves from the
// root. This is the recognition list: it widens LEGACY_ROOT_PAGES below, so a route added under
// src/app/[id]/ but missing here would be rendered by TSX with a slug as its hotel ID once Apache
// repoints the vhosts. `1sdp namespaces` in map1SDPToTSX.test.ts pins it against the directory listing.
// Both are migrated to TSX and own their route, so a legacy deep link keeps its suffix and lands on
// the same page the legacy service served - chrome included, which for these two is none: hotel-detail
// wrapped them in a PageLayout that renders head tags and tracking and no header or footer.
export const TSX_SUB_PAGES = ['hotel-static', 'hotel-reviews'] as const;

// The products hotel-detail served, in the order its own getPage() tested them. That order is
// load-bearing for the product scan in map1SDPToTSX; the order of LEGACY_ROOT_PAGES is not.
export const LEGACY_PRODUCTS = ['hotel-only', 'flight-hotel', 'train-hotel', 'sdp'] as const;

// Everything a legacy hotel URL can ask for. Derived, so a product added above cannot be accepted by
// the gate and then miss the product resolution in the mapper.
const LEGACY_ROOT_PAGES = [...LEGACY_PRODUCTS, ...TSX_SUB_PAGES];
const LEGACY_PAGES_GROUP = `(${LEGACY_ROOT_PAGES.join('|')})`;

// hotel-detail only ever served this namespace through one rewrite, /s/hdp/hotel-details/:path*
// (its next.config.js), so /s/hdp/hotel-details/<hotel>/<page> is the only shape production maps.
// Anything else - a product-first /s/hdp/<page>/<slug>, an unknown or wrong-cased page name, or no
// page segment at all - production answers 404, and so do we by not recognising it here. Matching on
// the substring /hotel-details alone used to send those to the DP default with an invented origin.
const HDP_HOTEL_PAGE = new RegExp(`^/s/hdp/hotel-details/[^/]+/${LEGACY_PAGES_GROUP}/?$`);

const isHdpUrl = (pathname: string): boolean => HDP_HOTEL_PAGE.test(pathname);

/**
 * hotel-detail's own entry namespace, which its own next.config rewrote onto /:path* internally
 * (see the hotel-detail repo). Apache hands it to TSX untouched, so TSX has to recognise it as-is.
 * Anchored, not a prefix match: a prefix would answer the legacy app's own /details/hotel/<slug>/api/*
 * XHR endpoints with a redirect into an SSR render, and its assets with a 400.
 */
const DETAILS_HOTEL_PAGE = new RegExp(`^${DETAILS_HOTEL_PREFIX}[^/]+/${LEGACY_PAGES_GROUP}/?$`);

export const isDetailsHotelUrl = (pathname: string): boolean => DETAILS_HOTEL_PAGE.test(pathname);

/**
 * Check if the request is a 1SDP URL pattern and needs to be redirected to TSX format.
 * The third namespace (legacy hotel pages served from the root) needs the request's basePath to be
 * told apart from TSX's own URLs, so it lives in isLegacyRootHotelUrl below.
 */
export const is1SDPUrl = (pathname: string): boolean => {
  const withoutLocale = stripLeadingLocale(pathname);
  return isHdpUrl(withoutLocale) || isDetailsHotelUrl(withoutLocale);
};

// Legacy hotel pages that hotel-detail serves straight from the root (see its src/pages/[hotel] routes):
// /21789/hotel-only, /Fairmont-El-San-Juan-Hotel-161205/flight-hotel, ... The hotel ID is either the
// whole first segment (the overwhelming majority of that traffic) or the last "-<digits>" run of the slug.
// `\\d` and not `\\d+`: the trailing `/` pins the digit run to the end of the segment either way, and
// `[^/]*\\d+` backtracks quadratically on a long all-digit path (165 ms for 16 KB, on a middleware with
// no matcher, so on every request).
const LEGACY_ROOT_HOTEL_PAGE = new RegExp(`^/[^/]*\\d/${LEGACY_PAGES_GROUP}/?$`);

export const isLegacyRootHotelPath = (pathname: string): boolean => LEGACY_ROOT_HOTEL_PAGE.test(pathname);

// The page a legacy URL asked for: always its last segment, in both anchored namespaces above.
export const getLegacyPageName = (pathname: string): string => {
  const trimmed = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed.slice(trimmed.lastIndexOf('/') + 1);
};

/**
 * hotel-static and hotel-reviews are ALSO live TSX routes (src/app/[id]/hotel-static,
 * src/app/[id]/hotel-reviews), so matching the pattern alone would turn /s/tsx/<id>/hotel-static into
 * a "1SDP URL" and redirect it onto itself. The basePath tells the two apart:
 * - build with BASE_PATH baked in: Next strips it off nextUrl.pathname and reports it in
 *   nextUrl.basePath, so a non-empty basePath means the request already addressed TSX.
 * - build without it: nextUrl.pathname keeps the /s/tsx prefix, and the anchored pattern rejects it
 *   because its first segment ("s") carries no digits.
 * Both checks together leave TSX's own pages untouched in either environment.
 */
export const isLegacyRootHotelUrl = ({ pathname, basePath }: { pathname: string; basePath: string }): boolean =>
  // Safe to strip the locale before matching even though basePath is the discriminator: BASE_PATH's
  // first segment is a single character ("s"), so the two-letter locale pattern never eats it.
  basePath === '' && isLegacyRootHotelPath(stripLeadingLocale(pathname));

// Reg exp to match results and details page
// Test cases
// '/'                  // true - RESULTS_PAGE
// '/123445'            // true - DETAILS_PAGE
// '/abc'               // false
// '/123abc'            // false
// '/123/456'           // false
export const isMainPagePathname = (pathname: string): boolean => {
  if (is1SDPUrl(pathname)) return true;
  const enumValues = Object.values(PAGE_ROUTE).join('|');
  const regex = new RegExp(`^/(\\d+|(${enumValues})(/\\d+)?)?$`);
  return regex.test(pathname);
};
